import { z } from "zod";
import { cardSchema, suitSchema } from "./cards";
import { deckIdSchema } from "./decks";
import { giftSchema, heldGiftSchema, reactionSchema } from "./social";
import { memberSchema, rulesSchema, sceneIdSchema, seatIndexSchema, seatInfoSchema, tableSettingsSchema, tableStatusSchema, tempoSchema, variantSchema, } from "./table";
import { PROTOCOL_VERSION } from "./version";
const v = z.literal(PROTOCOL_VERSION);
/** A game move on the wire. The engine validates the rule; this is only the shape. */
export const moveSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("bid"), amount: z.union([z.number().int(), z.literal("pass")]) }),
    z.object({ kind: z.literal("trump"), suit: suitSchema }),
    z.object({ kind: z.literal("play"), card: cardSchema }),
    // King: the chooser names the hand's game, a penalty game or a trump suit (review 0026).
    z.object({
        kind: z.literal("contract"),
        game: z.enum(["noTricks", "noHearts", "noBoys", "noQueens", "lastTwo", "rifki", "trump"]),
        suit: suitSchema.optional(),
    }),
    // Hearts: the three cards handed on before a hand (review 0025).
    z.object({ kind: z.literal("pass"), cards: z.array(cardSchema).length(3) }),
    // Baloot doubling: raise to the next level, or pass.
    z.object({ kind: z.literal("double"), raise: z.boolean() }),
    // Baloot: pass, sun, or hokum in a suit (the turned suit when omitted).
    z.object({
        kind: z.literal("call"),
        call: z.discriminatedUnion("kind", [
            z.object({ kind: z.literal("pass") }),
            z.object({ kind: z.literal("sun") }),
            z.object({ kind: z.literal("hokum"), suit: suitSchema.optional() }),
        ]),
    }),
]);
/** Client to server. Identity is not in the message: the connection carries it. */
export const clientMessageSchema = z.discriminatedUnion("type", [
    z.object({ v, type: z.literal("sit"), seat: seatIndexSchema }),
    z.object({ v, type: z.literal("stand") }),
    z.object({ v, type: z.literal("start") }),
    z.object({ v, type: z.literal("kick"), userId: z.string().min(1) }),
    z.object({ v, type: z.literal("again") }),
    /** Leave the table for good; mid-game a bot takes the seat. */
    z.object({ v, type: z.literal("leave") }),
    z.object({ v, type: z.literal("fillBots") }),
    /** "Take over": the player is back and plays their own seat again (decision 173). */
    z.object({ v, type: z.literal("takeOver") }),
    /** A quick message or emoji to the table (review 0022). */
    z.object({ v, type: z.literal("react"), reaction: reactionSchema }),
    /** A gift onto a seated player's picture; a new one replaces what they hold. */
    z.object({ v, type: z.literal("gift"), to: z.string().min(1), gift: giftSchema }),
    /** The holder taps their gift away. */
    z.object({ v, type: z.literal("clearGift") }),
    /** The owner opens the empty seats to "Find players", or closes them again (review 0020, 126 A). */
    z.object({ v, type: z.literal("queue"), open: z.boolean() }),
    /**
     * Discord told this client that these players left the Activity (slice 0024). Only a hint: the
     * server asks Discord itself before it acts (decision 116).
     */
    z.object({ v, type: z.literal("departed"), userIds: z.array(z.string().min(1)).min(1).max(4) }),
    z.object({ v, type: z.literal("move"), move: moveSchema }),
    /** The owner picks the game in the lobby; goal and length keep the engine defaults unless given. */
    z.object({
        v,
        type: z.literal("settings"),
        variant: variantSchema,
        goal: z.number().int().positive().nullable().optional(),
        length: z.number().int().positive().optional(),
        tempo: tempoSchema.optional(),
        /** Listed in the open tables for everyone, or reachable only by link and channel. */
        listed: z.boolean().optional(),
        /** House rules for the game; omitted keeps the table's, unknown keys are dropped. */
        rules: rulesSchema.optional(),
        /** The table's scene (review 0012 S1); omitted keeps the table's. */
        scene: sceneIdSchema.optional(),
        /** The table's deck (ADR 0029); the server checks the owner may use it. */
        deck: deckIdSchema.optional(),
    }),
    z.object({ v, type: z.literal("ping") }),
]);
export const gameEventSchema = z.object({
    code: z.string(),
    seat: seatIndexSchema.optional(),
    data: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
/** Server to client. `view` is the engine's per-seat body; the client renders it by variant. */
export const serverMessageSchema = z.discriminatedUnion("type", [
    z.object({
        v,
        type: z.literal("snapshot"),
        table: z.object({
            id: z.string(),
            status: tableStatusSchema,
            settings: tableSettingsSchema,
            owner: z.string().nullable(),
            seats: z.array(seatInfoSchema.nullable()).length(4),
            spectators: z.array(memberSchema),
            countdownMs: z.number().int().nonnegative().nullable(),
            scene: sceneIdSchema,
            /** Everyone at the table draws the cards from this deck (ADR 0029). */
            deck: deckIdSchema,
            listed: z.boolean(),
            /** Formed by "Find players" (review 0020): settings are the game's standard, players come from anywhere. */
            queue: z.boolean(),
            /** Gifts on seated players, by user id (review 0022). */
            gifts: z.record(z.string(), heldGiftSchema),
        }),
        /** `skus`: what you own (store items and Club); the client only decides what to show. */
        you: z.object({
            userId: z.string(),
            seat: seatIndexSchema.nullable(),
            skus: z.array(z.string()),
        }),
        view: z.unknown().nullable(),
        /** Remaining time for the acting seat; never an absolute timestamp. */
        turnMs: z.number().int().nonnegative().nullable(),
    }),
    z.object({ v, type: z.literal("events"), events: z.array(gameEventSchema) }),
    z.object({ v, type: z.literal("error"), code: z.string() }),
    z.object({ v, type: z.literal("pong") }),
    /** Someone seated reacted; shown by their pod for a moment, never stored. */
    z.object({ v, type: z.literal("reaction"), from: z.string().min(1), reaction: reactionSchema }),
]);
/** Narrows a raw client body; null when it is not a message we know. */
export function parseClientMessage(raw) {
    const result = clientMessageSchema.safeParse(raw);
    return result.success ? result.data : null;
}
/** Narrows a raw server body on the client side. */
export function parseServerMessage(raw) {
    const result = serverMessageSchema.safeParse(raw);
    return result.success ? result.data : null;
}
//# sourceMappingURL=messages.js.map